import { LoggerService } from './../common/services/logger.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  SocialAuthService,
  SocialUser,
} from '@abacritt/angularx-social-login';
import { UserContextService } from '../common/services/user-context.service';
import { UtilitiesService } from '../common/services/utilities.service';
import jwt_decode from 'jwt-decode';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { LANDING_ROUTE } from 'src/app/common/constants/dashboard-visibility';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser: SocialUser;
  loggedIn: Boolean;
  permissions: any;
  private unauthorizedHandling = false;
  /** In-flight refresh, shared so concurrent 401s don't each fire their own. */
  private refreshInFlight: Observable<boolean> | null = null;

  constructor(
    public socialAuthService: SocialAuthService,
    private router: Router,
    private http: HttpClient,
    private userContextService: UserContextService,
    private utilitiesService: UtilitiesService,
    private logger: LoggerService,
    private messageService: MessageService,
  ) {}

  /**
   * Retrieves Soscial User Data
   */
  getSocialUser() {
    this.currentUser = JSON.parse(localStorage.getItem('socialUser') || 'null');
    this.loggedIn = JSON.parse(localStorage.getItem('loggedIn') || 'false');
    if (!this.currentUser && this.loggedIn) {
      this.logout();
    }
    return { currentUser: this.currentUser, loggedIn: this.loggedIn };
  }

  /**
   * Retrieves if the user is loggedIn
   */
  public getLoggedIn() {
    return JSON.parse(localStorage.getItem('loggedIn') || 'false');
  }

  /**
   * Retrieves if the user is loggedIn
   */
  public loadedPermissions() {
    return this.permissions?.length > 0;
  }

  /**
   * Retrieves the user token from Local Storage
   */
  public getToken() {
    return localStorage.getItem('userToken');
  }

  /**
   * Sets user token to local storage.
   *
   * @param token The user toket to set.
   */
  public setToken(token: any) {
    localStorage.setItem('userToken', token);
    localStorage.setItem('token', token);
  }

  /**
   * Retrieves the refresh token from Local Storage.
   *
   * Access tokens are now short-lived, so the session is extended by presenting this
   * refresh token to the API. The API used to mint a token from any email posted to
   * /api/auth/refresh, which meant the client never had to hold a credential at all.
   */
  public getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  public setRefreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
  }

  /** Stores a freshly issued access + refresh pair. */
  private setSessionTokens(token: { accessToken?: string; refreshToken?: string } | undefined) {
    if (!token?.accessToken) return false;
    this.setToken(token.accessToken);
    if (token.refreshToken) {
      this.setRefreshToken(token.refreshToken);
    }
    return true;
  }

  /**
   * Sets socialUser to local storage.
   * This is temporary fix since https://github.com/abacritt/angularx-social-login/issues/522 still has an open issue on this....
   *
   * @param user socialUser
   */
  public setSocialUser(user: SocialUser) {
    localStorage.setItem('socialUser', JSON.stringify(user));
  }

  /**
   * Sets isLogged in to local storage.
   * This is temporary fix since https://github.com/abacritt/angularx-social-login/issues/522 still has an open issue on this....
   *
   * @param isLoggedIn The user toket to set.
   */
  public setLoggedIn(loggedIn: Boolean) {
    localStorage.setItem('loggedIn', JSON.stringify(loggedIn));
  }

  /**
   * User Login with Google
   */
  loginWithGoogle(token: string) {
    this.http.post<any>('/api/auth/login-google', { token })
      .pipe(
        tap((data: any) => this.handleLoginResponse(data)),
        catchError((error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Login Failed',
            detail: error?.error?.message || 'There was an error when trying to log in',
          });
          this.logout();
          return this.handleError(error)
        })
      )
      .subscribe();
  }

  private handleLoginResponse(data: any) {
    this.logger.log('login response', data);
    if (this.setSessionTokens(data?.token)) {
      // A full page load rather than router.navigate: AppComponent re-bootstraps here with
      // permissions loaded, and both the menu and AuthGuard depend on that. Assigning href
      // instead of reload() is what lands the user on the landing route rather than back on the
      // login screen at the current URL.
      window.location.href = LANDING_ROUTE;
      return;
    }
    // Handle unexpected response structure
    this.logger.error('Unexpected login response', data);
    window.location.reload();
  }

  private handleError(error: any) {
    this.logger.error('There was an error logging in.', error);
    // Return an observable to properly complete the observable chain
    return of(error);
  }

  /**
   * Checks if token is valid based on expires in
   * @param token
   * @returns
   */
  tokenValid(token: any) {
    // Access tokens are short-lived now (1h by default, was 30 days), so the renewal
    // window has to be minutes rather than a day — a 24h margin would mark every token
    // as due for renewal the moment it was issued.
    const RENEW_MARGIN_MS = 5 * 60 * 1000;
    return Date.now() < ((token.exp * 1000) - RENEW_MARGIN_MS);
  }

  /**
   * evaluateToken Token
   * verify time left to expire
   * if close to expire, renew
   */
  async evaluateToken() {
    let currentToken = this.getToken();
    if (currentToken) {
      let decoded = jwt_decode(currentToken);
      // this.logger.log('decoded: ', decoded);
      if (this.tokenValid(decoded)) {
        // this.logger.log('valid token');
      } else {
        this.refreshSession().subscribe((renewed) => {
          if (!renewed) {
            this.logger.log('logging out, token cannot be refreshed.');
            this.logout();
          }
        });
      }
    } else {
      this.logger.log('no token found');
      this.logout();
    }
  }


  /**
   * Exchanges the stored refresh token for a new session, at most once at a time.
   *
   * Access tokens are short-lived, so when one expires every in-flight request fails at once.
   * Without the single-flight guard each of those would start its own refresh, and since the
   * server rotates the refresh token on use, all but the first would present a token that had
   * just been superseded — turning one expiry into a logout. Concurrent callers share this
   * one request instead.
   *
   * Resolves `true` when the session was renewed, `false` when the caller should give up and
   * sign the user out. It never throws.
   */
  refreshSession(): Observable<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      // A session created before refresh tokens existed, or one already signed out.
      return of(false);
    }

    this.refreshInFlight = this.http.post<any>('/api/auth/refresh', { refreshToken }).pipe(
      map((data: any) => this.setSessionTokens(data?.token)),
      catchError((error: any) => {
        this.logger.error('Token refresh failed: ', error);
        return of(false);
      }),
      finalize(() => {
        this.refreshInFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshInFlight;
  }

  /**
   * Extends the CMS session using the stored refresh token.
   *
   * This no longer posts the Google social user to the API. That endpoint accepted any
   * body containing an email and signed a token for it, so the request carried no proof
   * of identity — the account it returned a session for was simply whichever email was in
   * the payload. The refresh token is a credential this server issued and can revoke.
   */
  async refreshToken(): Promise<boolean> {
    this.logger.log('Refreshing user token.');
    const renewed = await new Promise<boolean>((resolve) =>
      this.refreshSession().subscribe(resolve),
    );
    if (!renewed) {
      await this.logout();
    }
    return renewed;
  }

  /**
   * End the session after a 401. Used by the HTTP interceptor and the
   * notifications SSE client (raw fetch, so it bypasses the interceptor).
   * Idempotent — a second call while logout is already in flight is a no-op.
   */
  handleUnauthorizedSession(): void {
    if (!this.getLoggedIn() || this.unauthorizedHandling) return;
    this.unauthorizedHandling = true;
    void this.logout();
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  /**
   * Remove user token from local storage and redirect
   * user to the login screen.
   */
  async logout(redirectToLogin: boolean = true) {
    this.logger.log('logging out');
    this.setLoggedIn(false);

    await localStorage.removeItem('socialUser');
    await localStorage.removeItem('isGuestUser');
    await localStorage.removeItem('userToken');
    await localStorage.removeItem('token');
    await localStorage.removeItem('refreshToken');

    // Check if the current location is not login to prevent redirect loop
    if (redirectToLogin && window.location.pathname !== '/login') {
      this.utilitiesService.refreshComponent('home');

      this.logger.log('user token after logout', localStorage.getItem('userToken'));

      await Promise.race([
        this.socialAuthService.signOut().catch(() => {}),
        new Promise<void>(resolve => setTimeout(resolve, 3000)),
      ]);
      console.log('finished logging out');
      window.location.href = '/login';
    } else {
      // Optionally handle the case when already on the login page
      console.log('Already at the login page, no redirect performed.');
    }
  }

  /**
   * Check if read only user's token is still valid for the session.
   */
  async validateReadOnlyUserSession() {
    let isValid = false;

    let bgUser = this.getSocialUser();

    if (bgUser && bgUser.loggedIn) {
      isValid = true;
    } else {
      isValid = false;
    }

    return isValid;
  }

  /**
   * Check if user's token is still valid for the session.
   */
  async checkUserAuth() {
    let isValid = false;

    // Call Burlingame studios user method
    // Set this value to true, as we currently don't
    // have a way to verify the user's session is valid.
    // We can circle-back and integrate the new endpoint when we have it ready.
    isValid = true;

    return isValid;
  }

  loadPermissions(): Observable<any> {
    return this.http.get(`/api/permissions/user`).pipe(
      map((permissions: any) => {
        this.permissions = permissions;
        return permissions;
      }),
      shareReplay(1)
    );
  }

  hasPermission(abilities: string[], controller: string): boolean {
    if (!this.permissions) {
      return false;
    }
    
    const flattenedPermissions = this.permissions.flat();
    const relevantPermissions = flattenedPermissions.filter((permission: { subject: string; }) => permission.subject === controller);

    return abilities.every(action => relevantPermissions.some((permission: { action: string; }) => permission.action === action));
  }

  // Define the method to register a test user
  registerExternalUser(user: any): Observable<any> {
    const url = '/api/auth/guest/register';
    return this.http.post(url, user);
  }

  loginExternalUser(user: any) {
    const url = '/api/auth/guest/login';
    return this.http.post<any>(url, user).pipe(
      tap((response: { accessToken: any; refreshToken?: string; user: SocialUser; }) => {
        if (response && response.accessToken && response.user) {
          this.setLoggedIn(true)
          this.setSessionTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken });
          this.setSocialUser(response.user);
          this.userContextService.isGuestUser = true;
          window.location.reload();
        }
      })
    );
  }

  hasRole(roleName: string) {
    const currentToken = this.getToken();
    
    if (currentToken) {
      const decoded: any = jwt_decode(currentToken);
      return !!decoded?.roles?.find((role: any) => role.name === roleName);
    }

    return false;
  }
}
